/**
 * Question Bank Curator (Sheet 2 row 3) - pure UI-side helpers.
 *
 * Mirrors policylens-backend questionBankAiCurator.* and admin.controller.ts (commit 9572ac3).
 * PRD s2.2: masters / config surfaces are platform_admin-only - every /admin/question-bank route is
 * requireRole('platform_admin'); these predicates are UX affordances only, the API is the authority.
 * Commandment IV: AI drafts are not live until a human approves each one AND the version is published.
 * Commandment VII: a published question is never edited in place - revise/retire draft the next version.
 */
import { Role } from '../constants/roles';
import { userMessageForError } from './apiError';

/** Standards with a question bank (STANDARD_ID_TO_QUESTION_TAG in the backend). The API rejects any
 * other standardId with a 400, so the picker never offers one. */
export const CURATABLE_STANDARD_IDS = Object.freeze([
    'iso.27001-2022',
    'india.dpdpa-2023',
    'iso.27701-2019',
    'eu.gdpr-2016',
    'iso.42001-2023',
]);

/** Which Question column carries the clause reference per standard (CLAUSE_COLUMN_BY_TAG). */
export const CLAUSE_FIELD_BY_STANDARD = Object.freeze({
    'iso.27001-2022': 'isoControl',
    'india.dpdpa-2023': 'dpdpaSection',
    'iso.27701-2019': 'iso27701Clause',
    'eu.gdpr-2016': 'gdprArticle',
    'iso.42001-2023': 'iso42001Control',
});

export const PRIORITIES = Object.freeze(['High', 'Medium', 'Low']);

export const FIELD_LABELS = Object.freeze({
    question: 'Question text',
    responseType: 'Answer type',
    priority: 'Priority',
    isoControl: 'Clause (ISO 27001 control)',
    dpdpaSection: 'Clause (DPDP Act section)',
    iso27701Clause: 'Clause (ISO 27701)',
    gdprArticle: 'Clause (GDPR article)',
    iso42001Control: 'Clause (ISO 42001 control)',
    mandatory: 'Mandatory',
    evidenceRequired: 'Evidence required',
    expectedRespondent: 'Expected respondent',
    domain: 'Domain',
    severity: 'Severity',
    industryRelevance: 'Industry relevance',
    roleApplicability: 'Role applicability',
    gdprArticleResidence: 'Data subject residence',
    dataSubjectResidence: 'Data subject residence',
    aiRoleApplicability: 'AI role applicability',
    clauseRef: 'Clause',
    questionNo: 'Question number',
});

export const fieldLabel = (field) => FIELD_LABELS[field] || field;

export const DRAFT_STATE_LABELS = Object.freeze({
    AI_DRAFT: 'AI drafted - awaiting human review',
    APPROVED: 'Approved by a human - not live until published',
    REJECTED: 'Rejected',
    PUBLISHED: 'Published',
});

export const DRAFT_STATE_FILTERS = Object.freeze([
    { value: 'AI_DRAFT', label: 'Awaiting review' },
    { value: 'APPROVED', label: 'Approved, not yet published' },
    { value: 'REJECTED', label: 'Rejected' },
    { value: 'PUBLISHED', label: 'Published' },
    { value: 'ALL', label: 'All states' },
]);

export const canCurateQuestionBank = (role) => role === Role.ADMIN;

/** Select options for the standard picker: only curatable standards, labelled from the registry when it loaded. */
export function curatableStandardOptions(standards) {
    const byId = new Map((Array.isArray(standards) ? standards : []).filter((s) => s && typeof s.id === 'string').map((s) => [s.id, s.displayName]));
    return CURATABLE_STANDARD_IDS.map((id) => ({ value: id, label: byId.get(id) || id }));
}

export const clauseFieldFor = (standardId) => CLAUSE_FIELD_BY_STANDARD[standardId] || null;

// -- AI curation input ------------------------------------------------------------------------

export const LIMITS = Object.freeze({
    maxSources: 5,
    maxUrlSources: 3,
    minTextChars: 50,
    maxTextChars: 40000,
    maxTotalChars: 80000,
    maxNote: 1000,
});

/**
 * Licence / data-egress attestation the admin must tick before any source text leaves for the AI
 * service (the API refuses a run without attestedRightToProcess:true, 400 ATTESTATION_REQUIRED).
 * The server's own statement (error.details.statement) replaces this wording when it sends one.
 */
export const ATTESTATION_STATEMENT_FALLBACK = 'I have the right to process this text with a third-party AI service; it contains no licensed standards text I am not entitled to use';

/** Client-side required-field check; the server re-validates everything and stays the authority. */
export function validateCurateInput({ standardId, targetClause, sources, adminNote, attested }) {
    if (!standardId) return 'Choose a standard.';
    const clause = String(targetClause || '').trim();
    if (clause.length < 2 || clause.length > 200) return 'Enter the target clause (2 to 200 characters), for example A.5.15 or Section 8.';
    if (!Array.isArray(sources) || sources.length === 0) return 'Add at least one source: pasted text, an uploaded document or an approved URL.';
    if (sources.length > LIMITS.maxSources) return `Use at most ${LIMITS.maxSources} sources per run.`;
    if (sources.filter((s) => s.kind === 'url').length > LIMITS.maxUrlSources) return `Use at most ${LIMITS.maxUrlSources} URLs per run.`;
    let total = 0;
    for (let i = 0; i < sources.length; i += 1) {
        const s = sources[i];
        if (s.kind === 'url') {
            if (!/^https:\/\/\S+$/i.test(String(s.url || '').trim())) return `Source ${i + 1}: enter a full https:// URL.`;
        } else {
            const len = String(s.text || '').trim().length;
            if (len < LIMITS.minTextChars) return `Source ${i + 1}: add at least ${LIMITS.minTextChars} characters of text.`;
            total += Math.min(len, LIMITS.maxTextChars);
        }
    }
    if (total > LIMITS.maxTotalChars) return `Combined pasted text exceeds ${LIMITS.maxTotalChars.toLocaleString('en-IN')} characters.`;
    if (String(adminNote || '').length > LIMITS.maxNote) return `The note is limited to ${LIMITS.maxNote} characters.`;
    if (attested !== true) return 'Confirm that you have the right to process this text with a third-party AI service before drafting.';
    return null;
}

export function buildCuratePayload({ standardId, targetClause, sources, adminNote }) {
    const payload = {
        standardId,
        targetClause: String(targetClause).trim(),
        // Only reachable once validateCurateInput has seen the ticked attestation.
        attestedRightToProcess: true,
        sources: sources.map((s) => (s.kind === 'url'
            ? { kind: 'url', url: String(s.url).trim() }
            : { kind: 'text', text: s.text, ...(s.label && String(s.label).trim() ? { label: String(s.label).trim() } : {}) })),
    };
    const note = String(adminNote || '').trim();
    if (note) payload.adminNote = note;
    return payload;
}

// -- Error presentation (always through the shared apiError layer) ------------------------------

const FETCH_ERROR_TEXT = Object.freeze({
    HOST_NOT_ALLOWLISTED: 'This host is not on the approved source-domain list. Add it under Source domains, or paste the text instead.',
    PRIVATE_ADDRESS: 'This address points at a private or internal network, so it was refused.',
    REDIRECT_BLOCKED: 'The page redirected to a host that is not on the approved list, so it was refused.',
    TOO_MANY_REDIRECTS: 'The page redirected too many times.',
    BAD_URL: 'This is not a valid URL.',
    NOT_HTTPS: 'Only https:// URLs are accepted.',
    BAD_PORT: 'Only the standard https port is accepted.',
    DNS_FAILED: 'The host name could not be resolved.',
    TOO_LARGE: 'The page is larger than the size limit.',
    TIMEOUT: 'The host took too long to answer.',
    BAD_STATUS: 'The host answered with an error status.',
    UNSUPPORTED_CONTENT_TYPE: 'The page is not text or HTML (for example a PDF); paste its text instead.',
    EMPTY: 'The page contained no readable text.',
    NETWORK: 'The page could not be fetched.',
});

export function describeFetchRefusal(entry) {
    const reason = FETCH_ERROR_TEXT[entry?.errorCode] || entry?.error || 'The source could not be fetched.';
    return `${entry?.url || 'URL'} - ${reason}`;
}

/**
 * Turns a failed POST /ai-curate into what the screen shows. 400 and 429 carry the server's own
 * sentence; 422 lists each refused URL; 503 gets the shared layer's text (with its request reference)
 * plus the one fact the contract guarantees: no drafts were created.
 */
export function describeCurationError(err) {
    const status = err?.status;
    const base = userMessageForError(err, 'The AI run could not be completed.');
    if (status === 422) {
        const report = Array.isArray(err?.details?.fetchReport) ? err.details.fetchReport.filter((r) => r && r.ok === false) : [];
        return { kind: 'refused', message: base, refusals: report.map(describeFetchRefusal), refreshUsage: false };
    }
    if (status === 400 && err?.details?.code === 'ATTESTATION_REQUIRED') {
        const statement = typeof err.details.statement === 'string' && err.details.statement.trim() ? err.details.statement : null;
        return { kind: 'attestation', message: base, statement, refusals: [], refreshUsage: false };
    }
    if (status === 429) return { kind: 'capped', message: base, refusals: [], refreshUsage: true };
    if (status === 503) return { kind: 'model', message: base, note: 'No drafts were created.', refusals: [], refreshUsage: true };
    return { kind: 'error', message: base, refusals: [], refreshUsage: false };
}

/** Plain remaining-caps summary from GET /ai-curate/usage. */
export function summariseUsage(usage) {
    if (!usage || !usage.runs || !usage.tokens) return null;
    const runsLeft = Math.max(0, usage.runs.limit - usage.runs.used);
    const tokensLeft = Math.max(0, usage.tokens.limit - usage.tokens.used);
    return {
        runsLeft,
        tokensLeft,
        exhausted: runsLeft === 0 || tokensLeft === 0,
        runsText: `${usage.runs.used} of ${usage.runs.limit} runs used in the last 24 hours (${runsLeft} left)`,
        tokensText: `${usage.tokens.used.toLocaleString('en-IN')} of ${usage.tokens.limit.toLocaleString('en-IN')} tokens used (${tokensLeft.toLocaleString('en-IN')} left)`,
        perMinuteText: `Up to ${usage.perMinuteLimit} runs per minute`,
    };
}

// -- Draft / question state predicates ----------------------------------------------------------

/** The draft's Question row must still be a pending DRAFT for any review action (API loadReviewable). */
export const draftIsPending = (d) => !!d && d.questionStatus === 'DRAFT';
export const canApproveDraft = (d) => draftIsPending(d) && d.state === 'AI_DRAFT';
export const canRejectDraft = (d) => draftIsPending(d) && (d.state === 'AI_DRAFT' || d.state === 'APPROVED');
export const canEditDraft = (d) => draftIsPending(d) && (d.state === 'AI_DRAFT' || d.state === 'APPROVED');

/**
 * Splits one standard's rows (GET /admin/question-bank/questions returns every version) into what the
 * curator can act on, using the published/draft version ids from publish-preview.
 */
export function classifyQuestionRows(rows, publishedVersionId, draftVersionId) {
    const list = Array.isArray(rows) ? rows : [];
    const publishedNos = new Set(
        list.filter((r) => publishedVersionId && r.versionId === publishedVersionId && r.status === 'ACTIVE').map((r) => r.questionNo),
    );
    const pendingNos = new Set(
        list.filter((r) => draftVersionId && r.versionId === draftVersionId && (r.status === 'DRAFT' || r.status === 'RETIRED')).map((r) => r.questionNo),
    );
    const out = [];
    list.forEach((r) => {
        if (publishedVersionId && r.versionId === publishedVersionId && r.status === 'ACTIVE') {
            out.push({ ...r, rowKind: 'published', hasPendingChange: pendingNos.has(r.questionNo) });
        } else if (draftVersionId && r.versionId === draftVersionId && r.status === 'DRAFT') {
            out.push({ ...r, rowKind: 'draft', revisesPublished: publishedNos.has(r.questionNo) });
        } else if (draftVersionId && r.versionId === draftVersionId && r.status === 'RETIRED') {
            out.push({ ...r, rowKind: 'retiring', revisesPublished: publishedNos.has(r.questionNo) });
        }
    });
    return out;
}

/** Fields whose trimmed value differs from the current row; the revise / PATCH body carries only these. */
export function changedFields(row, edited, fields) {
    const out = {};
    fields.forEach((f) => {
        const next = String(edited[f] ?? '').trim();
        const cur = String(row[f] ?? '').trim();
        if (next !== cur) out[f] = next;
    });
    return out;
}

// -- Thin-row fields, approval readiness, break-glass -------------------------------------------

/** Fields a human must set before an AI draft can be approved (server: missingApprovalFields). */
export const THIN_ROW_FIELDS = Object.freeze(['domain', 'severity', 'mandatory', 'evidenceRequired', 'expectedRespondent']);
export const SEVERITIES = Object.freeze(['must', 'should', 'could']);
export const YES_NO = Object.freeze(['Y', 'N']);

/** The server's minimum for a break-glass reason (BREAK_GLASS_REASON_MIN). */
export const BREAK_GLASS_REASON_MIN = 15;
export const breakGlassReasonValid = (reason) => String(reason || '').trim().length >= BREAK_GLASS_REASON_MIN;

const labelList = (fields) => fields.map((f) => fieldLabel(typeof f === 'string' ? f : f.field)).join(', ');

/** Why Approve is disabled, from the server's readiness block; null when the server says it can be approved. */
export function approveBlockReason(draft) {
    // The API refuses an approve without the content hash (400 CONTENT_HASH_REQUIRED); never send one without it.
    if (draft && !draft.contentHash) return 'The server did not send this draft\'s content fingerprint, so it cannot be approved. Reload the draft.';
    const r = draft?.readiness;
    if (!r || r.canApprove !== false) return null;
    if (Array.isArray(r.missingFields) && r.missingFields.length > 0) {
        return `Complete these fields before approving: ${labelList(r.missingFields)}. Use Edit to set them.`;
    }
    if (r.contentProblem) return `The question text ${r.contentProblem}. Edit it to plain prose before approving.`;
    return 'This draft cannot be approved in its current state.';
}

export const clauseAckRequired = (draft) => draft?.readiness?.clauseAcknowledgementRequired === true;

/** True only when the server says so (flag on and exactly one platform admin); otherwise break-glass is not mentioned. */
export const approveBreakGlassAvailable = (draft) => draft?.readiness?.breakGlassAvailable === true;

/**
 * Approve body: always the contentHash the reviewer saw (mandatory server-side) plus the clause acknowledgement.
 * Break-glass is sent only when the server offers it for this draft and the reviewer chose it.
 */
export function buildApproveBody(draft, { acknowledgeClause = false, breakGlass = false, breakGlassReason = '' } = {}) {
    const body = { contentHash: draft?.contentHash };
    if (clauseAckRequired(draft) && acknowledgeClause) body.acknowledgeClauseNotInSource = true;
    if (breakGlass && approveBreakGlassAvailable(draft)) {
        body.breakGlass = true;
        body.breakGlassReason = String(breakGlassReason).trim();
    }
    return body;
}

const EXTRACT_TOO_LARGE_TEXT = 'This document is too large once unpacked; paste the text instead.';

/** Failed POST /ai-curate/extract-text. `retry` is set when the same file can simply be sent again. */
export function describeExtractError(err) {
    if (err?.status === 429 && err?.details?.code === 'EXTRACT_BUSY') {
        const n = Number(err.details.retryAfterSec);
        const wait = Number.isFinite(n) && n > 0 ? ` in ${n} second${n === 1 ? '' : 's'}` : ' shortly';
        return { message: `Another document is being processed; try again${wait}.`, retry: true };
    }
    if (err?.status === 400 && /too large once decompressed/i.test(err?.message || '')) {
        return { message: EXTRACT_TOO_LARGE_TEXT, retry: false };
    }
    return { message: userMessageForError(err, 'The document could not be read.'), retry: false };
}

/** Audit line for a recorded break-glass: who gave which reason and how the admin count was established. */
export function breakGlassAuditText(label, bg) {
    if (!bg) return null;
    const count = Number.isFinite(bg.adminCount) ? ` (platform admins counted: ${bg.adminCount}${bg.countSource ? `, source: ${bg.countSource}` : ''})` : '';
    return `${label}: ${bg.reason}${count}`;
}

const STALE_EDIT_TEXT = 'This question is no longer an editable draft: it was published or retired meanwhile. The list has been reloaded.';
/** 409 from PATCH / retire when the row is no longer a pending draft. The server sends no code, only this sentence. */
export const isStaleEditConflict = (err) => err?.status === 409 && /no longer an editable draft/i.test(err?.message || '');
export const staleEditMessage = () => STALE_EDIT_TEXT;

const errorCode = (err) => err?.details?.code || err?.code;

const IDENTITY_TEXT = 'Your account is not provisioned in PolicyLens, so your identity could not be resolved. Ask a platform admin to invite you.';
const isIdentityRefusal = (err) => err?.status === 403 && err?.code === 'FORBIDDEN' && !err?.details?.code;

/**
 * Turns a failed approve into screen text. `reload`: the draft on screen is stale, re-read it.
 * `unsafeField`: the column the server found to contain a link, email or markup (422 AI_DRAFT_UNSAFE_CONTENT).
 */
export function describeApproveError(err) {
    const code = errorCode(err);
    if (err?.status === 422 && code === 'AI_DRAFT_INCOMPLETE') {
        const missing = Array.isArray(err.details?.missingFields) ? err.details.missingFields : [];
        return { message: `Complete the draft before approving${missing.length ? `: ${labelList(missing)}` : ''}.`, reload: true };
    }
    if (err?.status === 422 && code === 'AI_DRAFT_UNSAFE_CONTENT') {
        const f = err.details?.field;
        return { message: f ? `The ${fieldLabel(f)} contains a link, email or markup and cannot be published. Edit it to plain text.` : userMessageForError(err), reload: false, unsafeField: f || null };
    }
    if (err?.status === 400 && code === 'CONTENT_HASH_REQUIRED') {
        return { message: 'The draft could not be approved because its content fingerprint was missing. It has been reloaded: review it and approve again.', reload: true };
    }
    if (err?.status === 409 && code === 'CLAUSE_NOT_IN_SOURCE_ACK_REQUIRED') {
        return { message: 'The clause reference does not appear in the cited source. Tick the acknowledgement to confirm it is correct, or edit the clause.', reload: true };
    }
    if (err?.status === 409 && code === 'CONTENT_CHANGED') {
        return { message: 'This draft changed since you opened it. It has been reloaded: review the current content and approve it again.', reload: true };
    }
    if (code === 'SEPARATION_OF_DUTIES') {
        return { message: 'A different platform admin must approve this draft, because you ran the curation that produced it.', reload: false };
    }
    if (code === 'BREAK_GLASS_DISABLED' || code === 'BREAK_GLASS_REFUSED') {
        return { message: 'A different admin must approve; break-glass is not enabled.', reload: false };
    }
    if (isIdentityRefusal(err)) return { message: IDENTITY_TEXT, reload: false };
    return { message: userMessageForError(err), reload: err?.status === 409 };
}

/** Same for a failed publish. `questionNos` are the questions the server names. */
export function describePublishError(err) {
    const code = errorCode(err);
    const nos = Array.isArray(err?.details?.questionNos) ? err.details.questionNos.filter(Boolean) : [];
    if (err?.status === 409 && code === 'CONTENT_CHANGED') {
        return { kind: 'content-changed', message: 'Some AI drafts changed after they were approved. They must be approved again before this version can be published.', questionNos: nos, reload: true };
    }
    if (code === 'SEPARATION_OF_DUTIES') {
        return { kind: 'separation', message: 'A different platform admin must publish this version, because you approved one or more of the AI drafts in it.', questionNos: [], reload: true };
    }
    if (code === 'BREAK_GLASS_DISABLED' || code === 'BREAK_GLASS_REFUSED') {
        return { kind: 'break-glass-refused', message: 'A different admin must publish; break-glass is not enabled.', questionNos: [], reload: true };
    }
    if (code === 'BREAK_GLASS_REASON_REQUIRED') {
        return { kind: 'break-glass-reason', message: `Break-glass needs a written reason of at least ${BREAK_GLASS_REASON_MIN} characters.`, questionNos: [], reload: false };
    }
    if (isIdentityRefusal(err)) return { kind: 'identity', message: IDENTITY_TEXT, questionNos: [], reload: false };
    return { kind: nos.length ? 'unapproved' : 'error', message: userMessageForError(err), questionNos: nos, reload: err?.status === 409 };
}

// -- Publish blockers (always the API's list; the UI only words them) ----------------------------

const BLOCKER_TEXT = Object.freeze({
    AI_DRAFT_UNAPPROVED: 'AI drafts that no person has approved yet. Approve or reject each one under Review AI drafts.',
    AI_DRAFT_CONTENT_CHANGED: 'AI drafts that were edited after they were approved. Review and approve them again.',
    SEPARATION_OF_DUTIES: 'AI drafts you approved yourself. A different platform admin must publish this version.',
});

/** One entry per blocker the server sent, with the question numbers it names when they can be resolved. */
export function describeBlockers(preview) {
    const items = [...(preview?.added || []), ...(preview?.revised || [])];
    const nosFor = (ids) => {
        const set = new Set(ids || []);
        return items.filter((i) => i.aiDraft && set.has(i.aiDraft.id)).map((i) => i.questionNo);
    };
    return (preview?.blockers || []).map((b) => ({
        code: b.code,
        text: BLOCKER_TEXT[b.code] || `Blocked: ${b.code}.`,
        questionNos: nosFor(b.draftIds),
        breakGlassAvailable: b.code === 'SEPARATION_OF_DUTIES' && b.breakGlassAvailable === true,
    }));
}

/** Break-glass can be offered only when every blocker is a separation-of-duties one and the server says it is available. */
export function breakGlassOffered(preview) {
    const blockers = preview?.blockers || [];
    return blockers.length > 0 && blockers.every((b) => b.code === 'SEPARATION_OF_DUTIES') && blockers.some((b) => b.breakGlassAvailable === true);
}

const pendingChanges = (preview) => {
    const s = preview?.summary;
    return s ? s.added + s.revised + s.retired : 0;
};

/**
 * Publish stays blocked while any blocker exists, unless the only blockers are separation-of-duties,
 * the server says break-glass is available, and the admin ticked it and wrote a valid reason.
 */
export function publishIsBlocked(preview, { breakGlass = false, breakGlassReason = '' } = {}) {
    if (!preview) return true;
    const blockers = Array.isArray(preview.blockers) ? preview.blockers : [];
    if (blockers.length === 0) return !preview.canPublish;
    if (!breakGlassOffered(preview)) return true;
    return !(breakGlass && breakGlassReasonValid(breakGlassReason) && pendingChanges(preview) > 0);
}

export function buildPublishBody({ breakGlass = false, breakGlassReason = '' } = {}) {
    return breakGlass ? { breakGlass: true, breakGlassReason: String(breakGlassReason).trim() } : undefined;
}
